import React, { MouseEvent, useState, useEffect } from "react";
import styled from "styled-components";

import { colors, colorsRGB, MOBILE_BREAKPOINT } from "@util/constants";
import { Container, P } from "@util/standard";
import { useStore } from "@state/store";
import { useMaximumBundleQuantity } from "@util/hooks";

const QuantityContainer = styled(Container) <{
  fontSize: string;
  mobileFontSize: string;
}>`
  border: 1px solid ${colors.lightGrayBackground};
  border-radius: 3px;
  font-size: ${({ fontSize }) => fontSize};

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize};
  }
`;
const QuantityButton = styled.button<{ disabled?: boolean }>`
  height: 2.5em;
  width: 2.5em;
  font-size: 1em;
  background: ${colorsRGB.merlin(0.15)};
  color: var(--theme-primary-color);
  text-align: center;
  cursor: pointer;
  border: 0;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `};
  &:hover {
    background: ${colorsRGB.merlin(0.3)};
  }
`;
const StyledInput = styled.input`
  box-sizing: border-box;
  width: 3.5em;
  height: 2.5em;
  padding: 0.7em;
  font-size: 1em;
  text-align: center;
  border: 0;
`;

interface Props {
  defaultQuantity?: number;
  updateLineItemFunc?: (
    id: string,
    quantity: number,
    change: number,
    shouldIncrease?: boolean
  ) => void;
  quantityMax?: number;
  fontSize?: string;
  mobileFontSize?: string;
  lineItemId?: string;
  resetOnShopifyUpdate?: boolean;
  disabled?: boolean;
  isBundleItem?: boolean;
  bundleId?: string;
  productId?: string;
}

const QuantityInput = ({
  defaultQuantity,
  updateLineItemFunc,
  quantityMax,
  fontSize,
  mobileFontSize,
  lineItemId,
  resetOnShopifyUpdate,
  disabled,
  isBundleItem,
  productId,
  bundleId,
}: Props) => {
  const maxBundleQuantity = bundleId && useMaximumBundleQuantity(bundleId);

  const { shopify } = useStore();
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(defaultQuantity ?? 1);
  const increment = (e: MouseEvent) => {
    e.preventDefault();

    if (bundleId && maxBundleQuantity == 0) {
      setError("You have reached the maximum available stock");
      return;
    }

    if (quantityMax && quantity >= quantityMax) {
      setError("You have reached the maximum available stock");
      return;
    }
    setError(null);
    if (updateLineItemFunc && lineItemId && defaultQuantity) {
      updateLineItemFunc(lineItemId, defaultQuantity, 1);
    }
    if (updateLineItemFunc && isBundleItem && productId && defaultQuantity) {
      updateLineItemFunc(productId, defaultQuantity, quantity + 1, true);
    }

    if (setQuantity) {
      setQuantity(quantity + 1);
    }
  };
  const decrement = (e: MouseEvent) => {
    e.preventDefault();
    setError(null);
    if (updateLineItemFunc && lineItemId && defaultQuantity) {
      updateLineItemFunc(lineItemId, defaultQuantity, -1);
    }
    if (updateLineItemFunc && isBundleItem && productId && defaultQuantity) {
      updateLineItemFunc(productId, defaultQuantity, quantity - 1);
    }
    if (setQuantity) {
      setQuantity(quantity < 1 ? 0 : quantity - 1);
    }
  };

  useEffect(() => {
    //This should be default quantity but for some reason default quantity isn't coming through
    const lineItem = shopify?.cart?.lines?.edges?.find(
      line => line?.node?.id === lineItemId
    );
    if (resetOnShopifyUpdate && lineItem) {
      setQuantity(lineItem.node.quantity);
    }
  }, [shopify]);

  return (
    <Container flexDirection="column" alignItems="flex-start">
      <QuantityContainer
        fontSize={fontSize ?? "14px"}
        mobileFontSize={mobileFontSize ?? "12px"}
        justifyContent="center"
        alignItems="center"
      >
        <QuantityButton
          disabled={disabled}
          name="quantity-decrease"
          onClick={decrement}
        >
          -
        </QuantityButton>
        {/* Use default quantity when on cart page, use useState on Product Form */}
        <StyledInput type="text" name="quantity" value={quantity} readOnly />
        <QuantityButton
          disabled={disabled}
          name="quantity-increase"
          onClick={increment}
        >
          &#xff0b;
        </QuantityButton>
      </QuantityContainer>
      {error && (
        <P fontSize={12} color="errorRed">
          {error}
        </P>
      )}
    </Container>
  );
};

export default QuantityInput;
